import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { setAppBadge, isBadgingSupported } from '@/lib/appBadge';

/*
  The query key the badge counts from. Exported because anything that marks a
  notification read has to invalidate it — the badge is otherwise correct only
  until the user reads something, and then stays wrong for a full poll interval
  on the one screen where they can see it is wrong.
*/
export const UNREAD_COUNT_KEY = ['/api/notifications/unread-count'] as const;

/*
  Deliberately UNSCOPED — no projectId.

  The bell in AppShell counts one project because it is rendered next to a
  project switcher and filters to the selection. A Home Screen icon has no
  such context: it is one badge for one installed app, and a user with three
  projects who sees "2" wants it to mean two things waiting for them, not two
  things waiting in whichever project happened to be selected when they last
  closed the app. `/api/notifications/unread-count` with no projectId returns
  the app-wide count (server/routes/notifications.ts), which is what this
  wants.

  Note the poll does NOT keep the session alive. Session renewal is an
  allow-list of exactly one endpoint (POST /api/auth/activity) precisely so
  that a query added later cannot silently defeat the 30-minute idle timeout —
  see the session-timeout section of CLAUDE.md. This is one of the queries
  that rule was written for.

  Every 60 seconds while the app is open and visible (React Query does not
  poll a hidden tab), and on every focus. It was every five minutes, which on
  the one surface showing the count meant a number up to five minutes stale
  after something arrived — PropPay's bell and badge refresh every minute.
*/
const POLL_MS = 60 * 1000;

export function useAppBadge(extraUnread: number = 0): void {
  const { user } = useAuth();

  const { data } = useQuery<{ count: number }>({
    queryKey: UNREAD_COUNT_KEY,
    // No point polling a browser that cannot render the result.
    enabled: !!user && isBadgingSupported(),
    refetchInterval: POLL_MS,
    // Refreshed on coming back to the app, but not by a glance at another
    // window: a minute's freshness. Marking anything read invalidates this
    // key, so the count never waits on it (QA 2026-10-28: every focus asked).
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  });

  const total = (data?.count ?? 0) + extraUnread;

  useEffect(() => {
    if (!isBadgingSupported()) return;

    /*
      No user is NOT a reason to clear the badge.

      It used to be: `!user` cleared the icon. But a session that idles out, or
      lapses while the app is shut, is the same person with the same unread
      work, who will be back — and on an installed app it is the normal state
      rather than an event. Clearing there told them their pending work was
      gone on the one surface they cannot correct without opening the app.

      The badge is cleared on a DELIBERATE sign-out only, by
      `clearBadgeForSignOut()` (lib/appBadge.ts), called from the sign-out
      path — the case where a count left on the icon would advertise one
      user's pending work to whoever picks the device up next.
    */
    if (!user) return;

    void setAppBadge(total);
  }, [user, total]);
}
